import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getAiEmployee, listAiEmployees } from '../services/aiEmployeeApi';
import { getCurrentOrgId } from '../services/auth/currentOrg';
import { purgeChatDraft } from '../utils/editorStateStorage';
import { purgeEmployeeLocalState } from '../utils/employeeLocalState';
import NotificationSoundManager from '../utils/NotificationSoundManager';
import UserStreamManager from '../utils/UserStreamManager';
import { useAuth } from './AuthContext';

/**
 * EmployeeListContext — the AI-Employee analog of ChatListContext, mounted ONCE globally (never
 * unmounts) so the sidebar employee list is snappy and survives route-driven remounts. Same dynamics as
 * the chat list:
 *   - addEmployeeToList(emp)      → instant prepend on hire (the sole add path).
 *   - updateEmployeeInList(meta)  → merge row metadata in place (update-if-present only).
 *   - setSelectedEmployeeId(id)   → ensure the open employee is in the list (splice-to-top if absent).
 *   - WS EMPLOYEE_UPDATED (carries row metadata from _publish_employee_event) → live row patch.
 *   - WS CHAT_DELETED (anchor id == employee_id) → remove the row.
 *   - reconnect → reconcile the active employee (per-id; employees are few, no bulk endpoint).
 *
 * Rows are keyed by `employee_id` (the anchor / employee_post_office_id). See
 * agent_docs/ai_employee_list_snappy_parity_design.md + employee_list_parity_execution_plan.md.
 */
const EmployeeListContext = createContext();

export const EmployeeListProvider = ({ children }) => {
  const [employees, setEmployees] = useState([]);
  // CEL-861 §5 (Employees sidebar): the org's team tree from GET /ai-employees — the WHOLE org
  // (humans + AI at true positions, multiple top-level people first-class). Personal orgs get
  // the same tree (one human, any number of AI employees — Nitish 08-04). null = no org →
  // the sidebar falls back to the flat AI hierarchy.
  const [orgTree, setOrgTree] = useState(null);
  // Org-wide visibility (agent_docs/employee_org_visibility_prd.md R2): the org's OTHER employees, the ones the
  // viewer can see but not instruct (lean rows, access "none"). Kept apart from `employees` on purpose: nothing that
  // iterates `employees` (sounds, attention, WS patches, search, routing) ever touches them.
  const [orgEmployees, setOrgEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser, userProfile } = useAuth();
  // Notifications v1: the viewer's CellCog user id, readable from the stable updateEmployeeInList callback.
  const profileIdRef = useRef(null);
  useEffect(() => { profileIdRef.current = userProfile?.id || null; }, [userProfile?.id]);

  const selectedEmployeeIdRef = useRef(null);
  const employeesRef = useRef(employees);
  const sidebarScrollPositionRef = useRef(0);

  useEffect(() => { employeesRef.current = employees; }, [employees]);

  const idOf = (e) => e?.employee_id || e?.id;

  const fetchEmployees = useCallback(async () => {
    if (!currentUser || userProfile?.onboarding_status !== 'JOINED') {
      setEmployees([]);
      setOrgTree(null);
      setOrgEmployees([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await listAiEmployees();
      const list = res.data?.employees || res.data || [];
      setOrgTree(res.data?.org_tree || null);
      setOrgEmployees(Array.isArray(res.data?.org_employees) ? res.data.org_employees : []);
      // If the open employee isn't on the list (just-created / race), splice it to the top.
      let finalList = list;
      const selId = selectedEmployeeIdRef.current;
      if (selId && !list.some((e) => idOf(e) === selId)) {
        try {
          const one = await getAiEmployee(selId);
          // Only an employee of the CURRENT org (Oct 27 2026: a moved employee kept reappearing in the old org's list).
          const cur = getCurrentOrgId();
          const org = one.data && one.data.organization_id;
          if (one.data && (!cur || !org || String(org) === String(cur))) finalList = [one.data, ...list];
        } catch (e) {
          // best-effort
        }
      }
      setEmployees(finalList);
    } catch (e) {
      // keep last good list on transient error
      console.error('[EmployeeList] fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [currentUser, userProfile?.onboarding_status]);

  // Instant prepend on hire (the sole add path; dedupe by employee_id).
  const addEmployeeToList = useCallback((emp) => {
    if (!emp) return;
    const id = idOf(emp);
    if (!id) return;
    setEmployees((prev) => {
      const exists = prev.some((e) => idOf(e) === id);
      return exists ? prev.map((e) => (idOf(e) === id ? { ...e, ...emp } : e)) : [emp, ...prev];
    });
  }, []);

  // Merge row metadata in place. UPDATE-IF-PRESENT only — arbitrary WS events never auto-add a row
  // (the hire flow's addEmployeeToList is the sole add path), so a stale event can't resurrect a row.
  // When a shift transitions operating→done (the PostOffice.stop completion event carries
  // operating=false), play the SAME completion sound as chats — mirroring ChatListContext's
  // operating→done detection. The sidebar row's `notification-sound-played` listener shows the bell.
  const updateEmployeeInList = useCallback((meta) => {
    const id = meta?.employee_id || meta?.id;
    if (!id) return;
    // Play the completion sound on the EXPLICIT shift-completed signal (PostOffice.stop emits
    // kind:shift, op:completed). This is deterministic — unlike a true→false `operating` transition,
    // which silently failed when the row had never seen operating=true (the list fetch / to_dict does
    // not include `operating`, so a shift already running when the list loaded had operating=undefined).
    // Notifications v1 (Sep 9 2026): op=completed says the stop is a ringing kind; attention_actor_id says WHO it
    // rings for (the human who messaged in the run; null = nobody, e.g. an autonomous rest). Absent key = old backend.
    const forMe = meta.attention_actor_id === undefined
      || (!!meta.attention_actor_id && meta.attention_actor_id === profileIdRef.current);
    if (meta.kind === 'shift' && meta.op === 'completed' && forMe) {
      NotificationSoundManager.play(id);
    }
    setEmployees((prev) =>
      prev.map((e) => (idOf(e) === id ? { ...e, ...meta } : e))
    );
  }, []);

  const removeEmployeeFromList = useCallback((id) => {
    if (!id) return;
    setEmployees((prev) => prev.filter((e) => idOf(e) !== id));
  }, []);

  const setSelectedEmployeeId = useCallback((id) => {
    selectedEmployeeIdRef.current = id;
    if (id && !employeesRef.current.some((e) => idOf(e) === id)) {
      // The open employee isn't loaded yet — fetch it and splice to top (mirrors chat splice-to-top).
      getAiEmployee(id)
        .then((res) => { if (res.data) addEmployeeToList(res.data); })
        .catch(() => {});
    }
  }, [addEmployeeToList]);

  // WS: live row metadata + deletion. (EMPLOYEE_UPDATED carries the sidebar row subset via
  // _publish_employee_event; CHAT_DELETED's chat_id == the employee anchor id.)
  useEffect(() => {
    if (!currentUser) return undefined;

    const unsubUpdated = UserStreamManager.subscribe('EMPLOYEE_UPDATED', (msg) => {
      if (msg && msg.employee_id) updateEmployeeInList(msg);
    });
    // CEL-861 §5: org structure changed (reassign, team create/rename/dissolve) → the sidebar's
    // tree is stale; debounce-refetch the list+tree (same notify-then-fetch shape as the org page).
    let orgTreeTimer = null;
    const unsubTeams = UserStreamManager.subscribe('ORG_TEAMS_CHANGED', () => {
      clearTimeout(orgTreeTimer);
      orgTreeTimer = setTimeout(() => { fetchEmployees(); }, 400);
    });
    const unsubDeleted = UserStreamManager.subscribe('CHAT_DELETED', (data) => {
      if (data?.chat_id) {
        // Employee drafts are scoped to the EMPLOYEE id (CEL-604), and this event's chat_id IS the
        // employee anchor id — so this is the draft key. Purge (not clear) so a still-mounted
        // composer's unmount flush cannot re-create it as an orphan.
        purgeChatDraft(data.chat_id);
        // Same funnel for the Employee Computer's browser-local records (persisted session,
        // window geometry, Chats pane width): both delete buttons and deletes from another tab
        // arrive here, so the keys never outlive the employee in this browser.
        purgeEmployeeLocalState(data.chat_id);
        removeEmployeeFromList(data.chat_id);
      }
    });
    const unsubReconnect = UserStreamManager.addReconciliationCallback(() => {
      const selId = selectedEmployeeIdRef.current;
      if (selId) {
        getAiEmployee(selId)
          .then((res) => { if (res.data) updateEmployeeInList({ ...res.data, employee_id: idOf(res.data) }); })
          .catch(() => {});
      }
    });

    return () => {
      unsubUpdated();
      unsubTeams();
      clearTimeout(orgTreeTimer);
      unsubDeleted();
      unsubReconnect();
    };
  }, [currentUser, updateEmployeeInList, removeEmployeeFromList, fetchEmployees]);

  // Initial fetch on login / enablement.
  useEffect(() => {
    if (currentUser && userProfile?.onboarding_status === 'JOINED') {
      fetchEmployees();
    } else if (currentUser && userProfile && userProfile.onboarding_status !== 'JOINED') {
      setEmployees([]);
      setLoading(false);
    }
  }, [currentUser, userProfile?.onboarding_status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <EmployeeListContext.Provider value={{
      employees,
      orgTree,
      orgEmployees,
      loading,
      refreshEmployees: fetchEmployees,
      addEmployeeToList,
      updateEmployeeInList,
      removeEmployeeFromList,
      setSelectedEmployeeId,
      sidebarScrollPositionRef,
    }}>
      {children}
    </EmployeeListContext.Provider>
  );
};

export const useEmployeeList = () => {
  const ctx = useContext(EmployeeListContext);
  if (!ctx) {
    // Safe no-op fallback if used outside the provider (defensive default).
    return {
      employees: [], orgTree: null, orgEmployees: [], loading: false, refreshEmployees: () => {},
      addEmployeeToList: () => {}, updateEmployeeInList: () => {},
      removeEmployeeFromList: () => {}, setSelectedEmployeeId: () => {},
      sidebarScrollPositionRef: { current: 0 },
    };
  }
  return ctx;
};

export default EmployeeListContext;
